import { T, useTranslate } from '@tolgee/react';
import { useFlag } from '@unleash/proxy-client-react';
import { Divider, Drawer, Dropdown, Menu } from 'antd';
import MenuItem from 'antd/lib/menu/MenuItem';
import { AnimatePresence, motion } from 'framer-motion';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import React from 'react';
import { useIntercom } from 'react-use-intercom';
import styled, { createGlobalStyle, css } from 'styled-components';

import {
  CrossSvg,
  LogoSvg,
  MenuArrowDownSvg,
  MenuSvg,
  PlusSvg,
  QuestionMarkSvg,
  SignOutSvg,
  UserSvg,
} from '../../assets/icons';
import ChartSvg from '../../assets/icons/ChartSvg';
import CuripodAISolidStarsSvg from '../../assets/icons/CuripodAISolidStarsSvg';
import CuripodDevSvg from '../../assets/icons/CuripodDevLogo';
import CuripodNormal from '../../assets/icons/CuripodNormal';
import FolderSvg from '../../assets/icons/FolderSvg';
import GroupSvg from '../../assets/icons/GroupSvg';
import HomeSvg from '../../assets/icons/HomeSvg';
import PriceSvg from '../../assets/icons/PriceSvg';
import TrainingSvg from '../../assets/icons/Training';
import UsageGraphSvg from '../../assets/icons/UsageGraphSvg';
import theme from '../../assets/theme';
import useAccessBadge, {
  LicenseCoarseLevels,
  LicenseFineLevels,
} from '../../hooks/useAccessBadge';
import { useBreakPoint } from '../../hooks/useBreakPoint';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useMetrics } from '../../hooks/useMetrics';
import { useOnOutsideClick } from '../../hooks/useOnOutsideClick';
import useRouterMatch from '../../hooks/useRouterMatch';
import useSignIn from '../../hooks/useSignIn';
import useSignOut from '../../hooks/useSignOut';
import useTenants from '../../hooks/useTenants';
import useWorkSpace from '../../hooks/useWorkSpace';
import AuthGuardProvider from '../../providers/AuthGuardProvider';
import {
  useHideIntercom,
  useIsIntercomOpen,
} from '../../providers/CustomIntercomStateProvider';
import { PremiumTestPrepPlaywallProvider } from '../../providers/PremiumTestPrepPlaywallProvider/PremiumTestPrepPlaywallProvider';
import { ById } from '../../services/backendService/types';
import { isProduction } from '../../utils/client';
import { getDestinationTenantFromUrl } from '../../utils/navigator';
import { zIndexes } from '../../utils/zIndexes';
import { Button } from '../_atoms/Button';
import { IconWrapper } from '../_atoms/IconWrapper';
import ProfileDefault from '../_atoms/ProfileDefault';
import AnonymisedMobileTopbar from '../_molecules/AnonymisedMobileTopbar';
import SmartProfile from '../_molecules/SmartProfile';
import CreateTeam from '../_organisms/CreateTeam/CreateTeam';
import InvitationModal from '../_organisms/InvitationModal/InvitationModal';
import NewLiveSideBar from '../_organisms/NewLiveSideBar';
import { TenantsMenuItem } from '../_organisms/NewLiveSideBar/NewLiveSideBar';
import HotjarSnippet from '../analytics/HotJar';
import {
  ButlerUniversityCaseStudyTitle,
  CaliforniaImpactCaseStudyTitle,
  CharlieMarshallCaseStudyTitle,
  FlourBluffISDCaseStudyTitle,
  InnovationsAcademyCaseStudyTitle,
  MichiganImpactCaseStudyTitle,
  SivellsBendCaseStudyTitle,
  SouthernCaliforniaImpactCaseStudyTitle,
  TexasImpactCaseStudyTitle,
  TexasImpactCaseStudyTitle2,
  TexasImpactCaseStudyTitle3,
  YsletaMiddleSchoolCaseStudyTitle,
} from '../LandingPageHome';
import { BaseTransition } from '../styled';

export default function NewAppLayout({
  children,
  onBackClick,
  allowUnAuthenticated,
  scrollOffset,
  breakPoint = 800,
}: {
  children: ReactNode;
  onBackClick?: () => void;
  allowUnAuthenticated?: boolean;
  scrollOffset?: number;
  breakPoint?: number;
}) {
  const isDesktop = useBreakPoint(breakPoint);
  const session = useSession();
  const router = useRouter();
  const isLoading = session.status === 'loading';
  const isAuthenticated = session.status === 'authenticated';

  return (
    <>
      <AuthGuardProvider allowUnAuthenticated={allowUnAuthenticated}>
        <PremiumTestPrepPlaywallProvider>
          <>
            {isDesktop && isAuthenticated && <NewLiveSideBar />}
            {!isDesktop && isAuthenticated && (
              <>
                <NewMobileTopBar onBackClick={onBackClick} scrollOffset={scrollOffset} />
                {/* <BottomBar /> */}
              </>
            )}
            {!isDesktop && !isAuthenticated && (
              <AnonymisedMobileTopbar
                close={() => router.back()}
                isLoadingAuth={isLoading}
              />
            )}
            {isDesktop && !isAuthenticated && (
              <NewAnonymisedTopbar isLoadingAuth={isLoading} />
            )}

            {children}
          </>
        </PremiumTestPrepPlaywallProvider>
      </AuthGuardProvider>
      <HotjarSnippet />
    </>
  );
}

const NewAnonymisedTopbar = ({ isLoadingAuth }: { isLoadingAuth: boolean }) => {
  const router = useRouter();
  const isDesktop = useBreakPoint(1200);
  const { signIn } = useSignIn();
  const metrics = useMetrics(true);
  const { t } = useTranslate();
  return (
    <Container>
      <WidthContainer>
        <GroupContainer $includeMargin>
          <ShortLogoContainer onClick={() => router.push('/')}>
            {!isProduction() ? <CuripodDevSvg /> : <LogoSvg />}
          </ShortLogoContainer>
          <TopBarDropdowns />
        </GroupContainer>
        {!isLoadingAuth && (
          <GroupContainer>
            <StyledLink
              style={{
                fontWeight: 'bold',
                fontSize: 18,
                color: theme.colors.black,
                marginRight: 20,
                cursor: 'pointer',
              }}
              onClick={() => signIn({ callbackUrl: '/' })}
            >
              {t('Log in')}
            </StyledLink>

            <Button
              styles={{
                fontWeight: 500,
                fontSize: 18,
              }}
              onClick={() => {
                metrics.logEvent('Navigate.SignUp');
                router.push({
                  pathname: '/signup',
                  query: {
                    callback: window.location.href,
                  },
                });
              }}
              type="primary"
            >
              {isDesktop
                ? t('signuplong', `Sign up - It's free`)
                : t('signup', 'Sign up')}
            </Button>
          </GroupContainer>
        )}
      </WidthContainer>
    </Container>
  );
};

const StyledLink = styled.span`
  font-weight: 500;

  &:hover {
    color: black !important;
  }
`;

const about = [
  {
    text: <T keyName="Our Learning Principles" defaultValue="Our Learning Principles" />,
    link: '/learning-principles',
  },
  { text: <T keyName="About Us" defaultValue="About Us" />, link: '/c/about-us' },
  {
    text: <T keyName="Careers" defaultValue="Careers" />,
    link: '/c/careers',
    external: true,
  },
];
export const caseStudies = [
  {
    text: TexasImpactCaseStudyTitle3,
    link: '/case-studies/curipod-case-study-green-valley',
  },
  {
    text: TexasImpactCaseStudyTitle2,
    link: '/case-studies/curipod-case-study-south-east-texas',
  },
  {
    text: SivellsBendCaseStudyTitle,
    link: '/case-studies/curipod-case-study-sivells-bend',
  },
  {
    text: TexasImpactCaseStudyTitle,
    link: '/case-studies/curipod-case-study-south-texas',
  },
  {
    text: YsletaMiddleSchoolCaseStudyTitle,
    link: '/case-studies/curipod-case-study-ysleta-bilingual',
  },
  {
    text: FlourBluffISDCaseStudyTitle,
    link: '/case-studies/curipod-case-study-flour-bluff',
  },
  {
    text: CharlieMarshallCaseStudyTitle,
    link: '/case-studies/curipod-case-study-charlie-marshall',
  },
  {
    text: SouthernCaliforniaImpactCaseStudyTitle,
    link: '/case-studies/curipod-case-study-southern-ca',
    external: false,
  },
  {
    text: CaliforniaImpactCaseStudyTitle,
    link: '/case-studies/california-writing-achievement',
    external: false,
  },
  {
    text: InnovationsAcademyCaseStudyTitle,
    link: '/case-studies/curipod-case-study-innovations-academy',
  },
  {
    text: MichiganImpactCaseStudyTitle,
    link: '/case-studies/curipod-case-study-michigan',
  },
  {
    text: ButlerUniversityCaseStudyTitle,
    link: '/case-studies/curipod-case-study-butler-university',
  },
];

export const independentResearch = [
  {
    text: (
      <T
        keyName="Curipod Upskills New Teachers and Reduces Planning Time by 30%"
        defaultValue="Curipod Upskills New Teachers and Reduces Planning Time by 30%"
      />
    ),
    link: '/research-studies/upskills-new-teachers',
  },
  {
    text: (
      <T
        keyName="Cooperative Learning With Curipod Drives Student Success"
        defaultValue="Cooperative Learning With Curipod Drives Student Success"
      />
    ),
    link: '/research-studies/cooperative-learning-boosts-success',
  },
  {
    text: (
      <T
        keyName="Increasing Science Class Engagement with Curipod"
        defaultValue="Increasing Science Class Engagement with Curipod"
      />
    ),
    link: '/research-studies/science-class-engagement',
  },
  {
    text: (
      <T
        keyName="AI Tools Improve Vocabulary and Engagement in Oman"
        defaultValue="AI Tools Improve Vocabulary and Engagement in Oman"
      />
    ),
    link: '/research-studies/vocabulary-engagement-oman',
  },
  {
    text: (
      <T
        keyName="AI-Powered Pedagogy Enhances Nursing Students' Competencies"
        defaultValue="AI-Powered Pedagogy Enhances Nursing Students' Competencies"
      />
    ),
    link: '/research-studies/ai-powered-pedagogy-nursing',
  },
];

const dropdownCaseStudies = [
  {
    text: TexasImpactCaseStudyTitle3,
    link: '/case-studies/curipod-case-study-green-valley',
  },
  {
    text: CaliforniaImpactCaseStudyTitle,
    link: '/case-studies/california-writing-achievement',
  },
];

export const qualityInstruction = [
  {
    text: (
      <T
        keyName="Raise Instructional Quality with Curipod"
        defaultValue="Raise Instructional Quality with Curipod"
      />
    ),
    link:
      '/c/blog-posts/solving-the-right-problems-how-school-leaders-can-raise-instructional-quality-with-curipod',
    external: false,
  },
  {
    text: (
      <T
        keyName="Raise Standardized Test Scores"
        defaultValue="Raise Standardized Test Scores"
      />
    ),
    link:
      '/c/blog-posts/from-test-prep-to-lasting-learning-how-to-raise-standardized-test-scores-without-burning-out-teachers-or-students',
    external: false,
  },
  {
    text: (
      <T
        keyName="Improve Writing Instruction Across the Curriculum"
        defaultValue="Improve Writing Instruction Across the Curriculum"
      />
    ),
    link:
      '/c/blog-posts/beyond-the-ela-classroom-how-to-improve-writing-instruction-across-the-curriculum',
    external: false,
  },
  {
    text: (
      <T
        keyName="AI Feedback that Fuels Learning"
        defaultValue="AI Feedback that Fuels Learning"
      />
    ),
    link: '/c/blog-posts/why-feedback-fuels-learning-and-how-curipod-makes-it-easy',
    external: false,
  },
  {
    text: (
      <T
        keyName="Universal Design for Learning (UDL)"
        defaultValue="Universal Design for Learning (UDL)"
      />
    ),
    link:
      '/c/blog-posts/designing-for-all-learners-how-curipod-supports-udl-in-the-classroom',
    external: false,
  },
  {
    text: (
      <T
        keyName="Students Looking Up From Their Screens"
        defaultValue="Students Looking Up From Their Screens"
      />
    ),
    link: '/c/blog-posts/why-were-betting-on-learning-together',
    external: false,
  },
  {
    text: (
      <T
        keyName="Language Learning That Sticks"
        defaultValue="Language Learning That Sticks"
      />
    ),
    link:
      '/c/blog-posts/language-learning-that-sticks-how-curipod-supports-multilingual-students',
    external: false,
  },
  {
    text: (
      <T
        keyName="Curipod + Bluebonnet Curriculum"
        defaultValue="Curipod + Bluebonnet Curriculum"
      />
    ),
    link: '/curriculum/bluebonnet',
    external: false,
  },
  {
    text: (
      <T
        keyName="Curipod + Savvas myView Literacy Texas"
        defaultValue="Curipod + Savvas myView Literacy Texas"
      />
    ),
    link: '/curriculum/savvas-myview',
    external: false,
  },
  {
    text: (
      <T
        keyName="Curipod + HMH Into Reading Texas"
        defaultValue="Curipod + HMH Into Reading Texas"
      />
    ),
    link: '/curriculum/hmh-into-reading',
    external: false,
  },
];
export const curriculum = [
  {
    text: <T keyName="Bluebonnet Learning" defaultValue="Bluebonnet Learning" />,
    link: '/phonics-practice/bluebonnet-learning',
    external: false,
  },
  {
    text: <T keyName="UFLI Foundations" defaultValue="UFLI Foundations" />,
    link: '/phonics-practice/ufli-foundations',
    external: false,
  },
  {
    text: <T keyName="Amplify CKLA" defaultValue="Amplify CKLA" />,
    link: '/phonics-practice/amplify-ckla',
    external: false,
  },
  {
    text: (
      <T
        keyName="Heggerty Phonemic Awareness"
        defaultValue="Heggerty Phonemic Awareness"
      />
    ),
    link: '/phonics-practice/heggerty-phonemic-awareness',
    external: false,
  },
  {
    text: <T keyName="Wilson Fundations" defaultValue="Wilson Fundations" />,
    link: '/phonics-practice/wilson-fundations',
    external: false,
  },
  {
    text: <T keyName="Wonders" defaultValue="Wonders" />,
    link: '/phonics-practice/wonders',
    external: false,
  },
  {
    text: <T keyName="HMH Into Reading" defaultValue="HMH Into Reading" />,
    link: '/phonics-practice/hmh-into-reading',
    external: false,
  },
  {
    text: (
      <T
        keyName="Really Great Reading Phonics Boost"
        defaultValue="Really Great Reading Phonics Boost"
      />
    ),
    link: '/phonics-practice/really-great-reading',
    external: false,
  },
  {
    text: <T keyName="All Phonics Practice" defaultValue="All Phonics Practice" />,
    link: '/phonics-practice',
    external: false,
  },
];

export const testPrep = [
  {
    text: (
      <T keyName="STAAR & TELPAS Test Prep" defaultValue="STAAR & TELPAS Test Prep" />
    ),
    link: '/c/staar-test-prep',
    external: false,
  },
  {
    text: (
      <T keyName="CAASPP & ELPAC Test Prep" defaultValue="CAASPP & ELPAC Test Prep" />
    ),
    link: '/c/caaspp-test-prep',
    external: false,
  },
  {
    text: <T keyName="GMAS Test Prep" defaultValue="GA Milestones Test Prep" />,
    link: '/c/gmas-test-prep',
    external: false,
  },
  {
    text: <T keyName="PSSA Test Prep" defaultValue="PSSA Test Prep" />,
    link: '/c/pssa-test-prep',
    external: false,
  },
  {
    text: <T keyName="NYSTP Test Prep" defaultValue="NYSTP Test Prep" />,
    link: '/c/nystp-test-prep',
    external: false,
  },
  {
    text: <T keyName="M-STEP Test Prep" defaultValue="M-STEP Test Prep" />,
    link: '/c/m-step-test-prep',
    external: false,
  },
  {
    text: <T keyName="NC State Tests" defaultValue="NC State Tests" />,
    link: '/c/nc-state-test-prep',
    external: false,
  },
  {
    text: <T keyName="Ohio State Tests" defaultValue="Ohio State Tests" />,
    link: '/c/ohio-state-test-prep',
    external: false,
  },
  {
    text: <T keyName="WIDA Test Prep" defaultValue="WIDA ACCESS Test Prep" />,
    link: '/c/wida-access-writing-prep',
    external: false,
  },
  {
    text: <T keyName="AASA Test Prep" defaultValue="AASA Test Prep" />,
    link: '/c/aasa-test-prep',
    external: false,
  },
  {
    text: <T keyName="ACAP Test Prep" defaultValue="ACAP Test Prep" />,
    link: '/c/acap-test-prep',
    external: false,
  },
  {
    text: <T keyName="Florida Test Prep" defaultValue="Florida Test Prep" />,
    link: '/c/florida-test-prep',
    external: false,
  },
  {
    text: <T keyName="TCAP Test Prep" defaultValue="TCAP Test Prep" />,
    link: '/c/tcap-test-prep',
    external: false,
  },
];

const support = [
  {
    text: <T keyName="Connectivity Issues" defaultValue="Connectivity Issues" />,
    link: '/connectivity-issues',
    external: false,
  },
];

const inspiration = [
  {
    text: <T keyName="Training and PD" defaultValue="Training and PD" />,
    link: '/c/training-and-professional-development',
  },
  {
    text: <T keyName="Moderation Tool" defaultValue="Moderation Tool" />,
    link: '/c/how-to-moderate-inappropriate-students-in-curipod',
    external: false,
  },
  {
    text: <T keyName="Webinars and Events" defaultValue="Webinars and Events" />,
    link: '/c/events',
  },
  { text: <T keyName="Blog" defaultValue="Blog" />, link: '/c/blogs' },
  {
    text: <T keyName="Teacher Community" defaultValue="Teacher Community" />,
    link: '/community',
  },
];

const compliance = [
  {
    text: <T keyName="AI in Curipod" defaultValue="AI in Curipod" />,
    link: 'https://curipod.com/c/safe-ai-in-curipod',
    external: true,
  },
  {
    text: <T keyName="Privacy Policy" defaultValue="Privacy Policy" />,
    link: '/privacy-policy',
  },
  {
    text: (
      <T
        keyName="State and District Privacy Agreements"
        defaultValue="State and District Privacy Agreements"
      />
    ),
    link: '/c/state-and-district-privacy-agreements',
  },
  {
    text: <T keyName="Terms of Use" defaultValue="Terms of Use" />,
    link: '/terms-of-use',
  },
  {
    text: <T keyName="Accessibility" defaultValue="Accessibility" />,
    link: '/accessibility',
    external: false,
  },
];

export const TopBarDropdowns = ({
  isLandingPage,
  isRightAligned,
}: {
  isLandingPage?: boolean;
  isRightAligned?: boolean;
}) => {
  const { t } = useTranslate();
  const { show } = useIntercom();
  const [impactDropdownVisible, setImpactDropdownVisible] = useState(false);

  // Listen for custom event to open Impact & Efficacy dropdown
  useEffect(() => {
    const handleOpenDropdown = () => {
      setImpactDropdownVisible(true);
    };

    window.addEventListener('openImpactEfficacyDropdown', handleOpenDropdown);
    return () => {
      window.removeEventListener('openImpactEfficacyDropdown', handleOpenDropdown);
    };
  }, []);

  return (
    <>
      <GlobalStyles $isLandingPage={isLandingPage} $isRightAligned={isRightAligned} />
      <Dropdown
        overlayClassName="color-dropdown"
        overlay={
          <Menu>
            <MenuContainer $isLandingPage={isLandingPage}>
              <DropdownMenuColumn>
                <h3>
                  <T
                    keyName="High Quality Instruction"
                    defaultValue="High Quality Instruction"
                  />
                </h3>
                {qualityInstruction.map(item => (
                  <Link href={item.link} passHref key={item.link}>
                    {item.external ? (
                      <a target="__blank" rel="noopener noreferrer">
                        <MenuItem>{item.text}</MenuItem>
                      </a>
                    ) : (
                      <a>
                        <MenuItem>{item.text}</MenuItem>
                      </a>
                    )}
                  </Link>
                ))}
              </DropdownMenuColumn>
              <Divider type="vertical" />
              <DropdownMenuColumn>
                <h3>
                  <T keyName="Phonics Practice" defaultValue="Phonics Practice" />
                </h3>
                {curriculum.map(item => (
                  <Link href={item.link} passHref key={item.link}>
                    {item.external ? (
                      <a target="__blank" rel="noopener noreferrer">
                        <MenuItem>{item.text}</MenuItem>
                      </a>
                    ) : (
                      <a>
                        <MenuItem>{item.text}</MenuItem>
                      </a>
                    )}
                  </Link>
                ))}
              </DropdownMenuColumn>
              <Divider type="vertical" />
              <DropdownMenuColumn>
                <h3>
                  <T keyName="Test Prep" defaultValue="Test Prep" />
                </h3>
                {testPrep.map(item => (
                  <Link href={item.link} passHref key={item.link}>
                    {item.external ? (
                      <a target="__blank" rel="noopener noreferrer">
                        <MenuItem>{item.text}</MenuItem>
                      </a>
                    ) : (
                      <a>
                        <MenuItem>{item.text}</MenuItem>
                      </a>
                    )}
                  </Link>
                ))}
              </DropdownMenuColumn>
            </MenuContainer>
          </Menu>
        }
      >
        <a onClick={e => e.preventDefault()}>
          <HeaderButton
            styles={{
              boxShadow: 'none',
              borderRadius: '50px',
              border: 'none',
              color: isLandingPage
                ? theme.colors.landingPageTopbarColor
                : theme.colors.black,
              fontSize: '16px',
            }}
            type={'ghost'}
            icon={<MenuArrowDownSvg />}
          >
            {t('Solutions')}
          </HeaderButton>
        </a>
      </Dropdown>
      <Dropdown
        overlayClassName="color-dropdown"
        placement="bottomLeft"
        open={impactDropdownVisible}
        onVisibleChange={setImpactDropdownVisible}
        overlay={
          <Menu>
            <MenuContainer $isLandingPage={isLandingPage}>
              <DropdownMenuColumn>
                <h3>
                  <T
                    keyName="Curipod Impact Studies"
                    defaultValue="Curipod Impact Studies"
                  />
                </h3>
                {dropdownCaseStudies.map(item => (
                  <Link href={item.link} passHref key={item.link}>
                    <a>
                      <MenuItem>{item.text}</MenuItem>
                    </a>
                  </Link>
                ))}
                <Link href="/case-studies" passHref>
                  <a>
                    <ViewAllLink>
                      <T
                        keyName="View all case studies & research"
                        defaultValue="View all case studies & research"
                      />{' '}
                      &rarr;
                    </ViewAllLink>
                  </a>
                </Link>
              </DropdownMenuColumn>
            </MenuContainer>
          </Menu>
        }
      >
        <a onClick={e => e.preventDefault()}>
          <HeaderButton
            styles={{
              boxShadow: 'none',
              borderRadius: '50px',
              border: 'none',
              color: isLandingPage
                ? theme.colors.landingPageTopbarColor
                : theme.colors.black,
              fontSize: '16px',
            }}
            type={'ghost'}
            icon={<MenuArrowDownSvg />}
          >
            <T keyName="Impact & Efficacy" defaultValue="Impact & Efficacy" />
          </HeaderButton>
        </a>
      </Dropdown>
      <Dropdown
        overlayClassName="color-dropdown"
        placement="bottomCenter"
        overlay={
          <Menu>
            <MenuContainer $isLandingPage={isLandingPage}>
              <DropdownMenuColumn>
                <h3>
                  <T keyName="Support" defaultValue="Support" />
                </h3>
                <MenuItem key={'support'} onClick={() => show()}>
                  <span>
                    <T
                      keyName="We want to hear from you!"
                      defaultValue="We want to hear from you!"
                    />
                  </span>
                </MenuItem>
                {support.map(item => (
                  <Link href={item.link} passHref key={item.link}>
                    {item.external ? (
                      <a target="__blank" rel="noopener noreferrer">
                        <MenuItem>{item.text}</MenuItem>
                      </a>
                    ) : (
                      <a>
                        <MenuItem>{item.text}</MenuItem>
                      </a>
                    )}
                  </Link>
                ))}
              </DropdownMenuColumn>
              <Divider type="vertical" />
              <DropdownMenuColumn>
                <h3>
                  <T keyName="Inspiration" defaultValue="Inspiration" />
                </h3>
                {inspiration.map(item => (
                  <Link href={item.link} passHref key={item.link}>
                    {item.external ? (
                      <a target="__blank" rel="noopener noreferrer">
                        <MenuItem>{item.text}</MenuItem>
                      </a>
                    ) : (
                      <a>
                        <MenuItem>{item.text}</MenuItem>
                      </a>
                    )}
                  </Link>
                ))}
              </DropdownMenuColumn>
              <Divider type="vertical" />
              <DropdownMenuColumn>
                <h3>
                  <T keyName="Compliance" defaultValue="Compliance" />
                </h3>
                {compliance.map(item => (
                  <Link href={item.link} passHref key={item.link}>
                    {item.external ? (
                      <a target="__blank" rel="noopener noreferrer">
                        <MenuItem>{item.text}</MenuItem>
                      </a>
                    ) : (
                      <a>
                        <MenuItem>{item.text}</MenuItem>
                      </a>
                    )}
                  </Link>
                ))}
              </DropdownMenuColumn>
              <Divider type="vertical" />
              <DropdownMenuColumn>
                <h3>
                  <T keyName="About" defaultValue="About" />
                </h3>
                {about.map(item => (
                  <Link href={item.link} passHref key={item.link}>
                    {item.external ? (
                      <a target="__blank" rel="noopener noreferrer">
                        <MenuItem>{item.text}</MenuItem>
                      </a>
                    ) : (
                      <a>
                        <MenuItem>{item.text}</MenuItem>
                      </a>
                    )}
                  </Link>
                ))}
              </DropdownMenuColumn>
            </MenuContainer>
          </Menu>
        }
      >
        <a onClick={e => e.preventDefault()}>
          <HeaderButton
            styles={{
              boxShadow: 'none',
              borderRadius: '50px',
              border: 'none',
              color: isLandingPage
                ? theme.colors.landingPageTopbarColor
                : theme.colors.black,
              fontSize: '16px',
            }}
            type={'ghost'}
            icon={<MenuArrowDownSvg />}
          >
            {t('Resources')}
          </HeaderButton>
        </a>
      </Dropdown>
      <Link href="/pricing" passHref>
        <a>
          <HeaderButton
            styles={{
              boxShadow: 'none',
              borderRadius: '50px',
              border: 'none',
              color: isLandingPage
                ? theme.colors.landingPageTopbarColor
                : theme.colors.black,
              fontSize: '16px',
            }}
            type={'ghost'}
          >
            <T keyName="Pricing" defaultValue="Pricing" />
          </HeaderButton>
        </a>
      </Link>
    </>
  );
};

const HeaderButton = styled(Button)`
  box-shadow: none;
  border-radius: 50px;
  border: none;

  svg {
    height: 12px;
    width: 12px;
    margin-right: 8px;
  }

  @media (max-width: 1120px) {
    padding: 10px 15px;
  }
`;

const maps: ById<string> = {
  search: 'Search',
  feed: 'Feed',
  profile: 'Profile',
  snippets: 'Snippets',
  queue: 'Answer',
  answer: 'Answer',
  review: 'Review',
  notifications: 'Notifications',
  feedback: 'Feedback',
  lessons: 'My lessons',
  workspace: 'Workspace',
  results: 'My reports',
  reports: 'My reports',
};
const MobilePageTitlesByRoute = (pathname: string) => {
  const nonQueryPathName = pathname.replace(/\?.*/, ''); // removes everything after a `?` sign
  const splittedPath = nonQueryPathName.split('/');
  if (splittedPath.includes('lessons'))
    return <T keyName="My lessons" defaultValue="My lessons" />;
  if (splittedPath.includes('usage'))
    return <T keyName="Your school usage" defaultValue="Your school usage" />;
  if (splittedPath.includes('results'))
    return <T keyName="My reports" defaultValue="My reports" />;
  if (splittedPath.includes('reports'))
    return <T keyName="My reports" defaultValue="My reports" />;
  if (splittedPath.includes('preview'))
    return <T keyName="Back to lesson" defaultValue="Back to lesson" />;
  if (splittedPath.includes('discover'))
    return <T keyName="Discover" defaultValue="Discover" />;
  if (splittedPath.includes('workspace'))
    return <T keyName="Workspace" defaultValue="Workspace" />;
  if (splittedPath.includes('templates'))
    return <T keyName="My lessons" defaultValue="My lessons" />;
  return maps[splittedPath[splittedPath.length - 1]] as string;
};
const NewMobileTopBar = ({
  onBackClick,
  scrollOffset,
}: {
  onBackClick?: () => void;
  scrollOffset?: number;
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { router, tenantId } = useRouterMatch();
  const mobileMenuTitle = MobilePageTitlesByRoute(router.asPath);
  const isRoot = router.asPath === `/`;
  const isTransparent = scrollOffset !== undefined ? scrollOffset <= 65 : undefined;
  return (
    <MobileContainer
      style={{
        padding: isTransparent ? '14px 20px 14px 20px' : '5px 15px 5px 15px',
        backgroundColor: isTransparent ? 'transparent' : theme.colors.topbar,
        boxShadow: isTransparent ? 'none' : '0px 4px 4px rgb(0, 0, 0, 0.1)',
      }}
      $isTransparent={isTransparent}
    >
      <RootGroupContainer
        onClick={() => {
          if (isRoot) {
            router.push(`/`);
          } else {
            onBackClick ? onBackClick() : router.back();
          }
        }}
      >
        <IconWrapper
          height={'25px'}
          width={'85px'}
          style={{
            color: isTransparent ? theme.colors.white : theme.colors.orange,
          }}
        >
          <LogoSvg />
        </IconWrapper>
      </RootGroupContainer>
      <h1
        style={{
          margin: '0 auto',
          fontSize: '18px',
          fontWeight: 'bold',
        }}
      >
        {mobileMenuTitle}
      </h1>

      <ClickableIconWrapper
        $hasBackground={isTransparent}
        onClick={() => setIsMenuOpen(true)}
      >
        <MenuSvg />
      </ClickableIconWrapper>
      <MobileMenu
        open={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        tenantId={tenantId}
      />
    </MobileContainer>
  );
};

const MobileCloseContext = React.createContext<{ onClose: () => void }>({
  onClose: () => undefined,
});

const MobileCreateLessonMenuItem = () => {
  const { router, tenantId, parentFolderId, workspaceId } = useRouterMatch();
  const metrics = useMetrics();
  const { trackEvent } = useIntercom();

  const createLessonLink = useMemo(() => {
    if (parentFolderId && workspaceId)
      return `/${tenantId}/lessons/new/edit?workspaceId=${workspaceId}&parentFolderId=${parentFolderId}`;
    if (workspaceId) return `/${tenantId}/lessons/new/edit?workspaceId=${workspaceId}`;
    if (parentFolderId)
      return `/${tenantId}/lessons/new/edit?parentFolderId=${parentFolderId}`;
    return `/${tenantId}/home`;
  }, [parentFolderId, tenantId, workspaceId]);

  const handleCreateTemplateMobile = () => {
    metrics.logEvent('Lessons.NewTemplate.Start', {
      path: router.pathname,
      placement: 'mobileSidebar',
      workspaceId: workspaceId,
    });
    router.replace(createLessonLink);
    trackEvent('PresentationCreated');
  };
  return (
    <MobileIconLinkContainer
      style={{ height: 42 }}
      onClick={() => {
        handleCreateTemplateMobile();
      }}
      isActive={router.asPath === `/${tenantId}/home`}
    >
      <MobileIconWrapper>
        <HomeSvg />
      </MobileIconWrapper>{' '}
      <p style={{ marginBottom: 0 }}>
        <T keyName="Home" defaultValue="Home" />
      </p>
    </MobileIconLinkContainer>
  );
};

type MobileMenuProps = {
  open: boolean;
  onClose: () => void;
  tenantId: string;
};
const MobileMenu = ({ open, onClose, tenantId }: MobileMenuProps) => {
  const { t } = useTranslate();
  const { accessBadge: userAccessBadge } = useAccessBadge();
  const { workspace, isFetching } = useWorkSpace();
  const { signOutAuto } = useSignOut();
  const handleSignOut = () => {
    // Use the shared flow — signOutAuto() clears the post-auth redirect itself.
    signOutAuto();
  };
  const { router } = useRouterMatch();
  const metrics = useMetrics();
  // Same gates as the desktop sidebar's profile menu, so the settings pages are
  // reachable on both and nobody is offered a link the page would bounce them
  // from: every teacher, never students.
  const isStudent = userAccessBadge?.user?.profileInfo?.role === 'student';
  const isBackofficeEnabled = useFeatureFlag('IN_PRODUCT_BACKOFFICE');
  const [showCreateWorkspacePopup, setShowCreateWorkspacePopup] = useState(false);
  const { isOpen } = useIsIntercomOpen();
  const { show, hide } = useIntercom();
  const handleCreateTeam = () => {
    metrics.logEvent('CreateTeam', { from: 'mobile' });
    setShowCreateWorkspacePopup(true);
  };

  const handleHelpClicked = () => {
    if (isOpen) {
      hide();
    } else {
      show();
    }
  };
  return (
    <Drawer
      placement="right"
      closable={false}
      onClose={onClose}
      open={open}
      key="left"
      drawerStyle={{ backgroundColor: theme.colors.paleBeige, color: theme.colors.black }}
    >
      <CreateTeam
        onDone={() => {
          setShowCreateWorkspacePopup(false);
          onClose();
        }}
        open={showCreateWorkspacePopup}
      />
      <MobileCloseContext.Provider value={{ onClose }}>
        <MobileMenuItemsContainer>
          <div>
            <HeaderMenuContainer key="1">
              <MainLogo />
              <IconClickWrapper onClick={onClose}>
                <CrossSvg />
              </IconClickWrapper>
            </HeaderMenuContainer>
            <TenantsMenuItem />
            <MobileCreateLessonMenuItem />
            <Link href={`/${tenantId}/lessons`}>
              <a onClick={onClose}>
                <MobileIconLinkContainer
                  isActive={router.asPath === `/${tenantId}/lessons`}
                >
                  <MobileIconWrapper>
                    <FolderSvg />
                  </MobileIconWrapper>{' '}
                  <p style={{ marginBottom: 0 }}>
                    <T keyName="My lessons" defaultValue="My lessons" />
                  </p>
                </MobileIconLinkContainer>
              </a>
            </Link>
            <Link href={`/${tenantId}/reports`}>
              <a onClick={onClose}>
                <MobileIconLinkContainer
                  isActive={router.asPath === `/${tenantId}/reports`}
                >
                  <MobileIconWrapper>
                    <ChartSvg />
                  </MobileIconWrapper>{' '}
                  <p style={{ marginBottom: 0 }}>
                    <T keyName="Reports" defaultValue="Reports" />
                  </p>
                </MobileIconLinkContainer>
              </a>
            </Link>
            {userAccessBadge?.organization?.country === 'US' && (
              <Link href={`/${tenantId}/usage`}>
                <a onClick={onClose}>
                  <MobileIconLinkContainer
                    isActive={router.asPath === `/${tenantId}/usage`}
                  >
                    <MobileIconWrapper>
                      <UsageGraphSvg />
                    </MobileIconWrapper>{' '}
                    <p style={{ marginBottom: 0 }}>
                      <T keyName="Your school usage" defaultValue="Your school usage" />
                    </p>
                  </MobileIconLinkContainer>
                </a>
              </Link>
            )}
            {/* Mirrors SidebarProfileMenu on desktop. Without these the
                profile-settings and organization-settings pages have no mobile
                entry point at all, since the sidebar (and its profile menu) is
                swapped out for this drawer below the mobile breakpoint.

                No "Profile" entry: the old /{tenantId}/profile page is on its way
                out, and everything it carried now lives on Settings. */}
            <Link href={`/${tenantId}/profile-settings`}>
              <a onClick={onClose}>
                <MobileIconLinkContainer
                  isActive={router.asPath === `/${tenantId}/profile-settings`}
                >
                  <MobileIconWrapper>
                    <UserSvg />
                  </MobileIconWrapper>{' '}
                  <p style={{ marginBottom: 0 }}>
                    <T keyName="Settings" defaultValue="Settings" />
                  </p>
                </MobileIconLinkContainer>
              </a>
            </Link>
            {!isStudent && (
              <Link href={`/${tenantId}/organization-settings`}>
                <a onClick={onClose}>
                  <MobileIconLinkContainer
                    isActive={router.asPath.startsWith(
                      `/${tenantId}/organization-settings`,
                    )}
                  >
                    <MobileIconWrapper>
                      <GroupSvg />
                    </MobileIconWrapper>{' '}
                    <p style={{ marginBottom: 0 }}>
                      <T
                        keyName="Organization settings"
                        defaultValue="Organization settings"
                      />
                    </p>
                  </MobileIconLinkContainer>
                </a>
              </Link>
            )}
            {/* Staff back office, same flag as the sidebar's Internal submenu. */}
            {isBackofficeEnabled && (
              <Link href="/admin/impersonate">
                <a onClick={onClose}>
                  <MobileIconLinkContainer
                    isActive={router.asPath === '/admin/impersonate'}
                  >
                    <MobileIconWrapper>
                      <UserSvg />
                    </MobileIconWrapper>{' '}
                    <p style={{ marginBottom: 0 }}>
                      <T keyName="Impersonate User" defaultValue="Impersonate User" />
                    </p>
                  </MobileIconLinkContainer>
                </a>
              </Link>
            )}
            <Link href={`/pricing`}>
              <a onClick={onClose}>
                <MobileIconLinkContainer isActive={router.asPath === `/pricing`}>
                  <MobileIconWrapper>
                    <PriceSvg />
                  </MobileIconWrapper>{' '}
                  <p style={{ marginBottom: 0 }}>{t('Pricing')}</p>
                </MobileIconLinkContainer>
              </a>
            </Link>
            <Link href={`/learn`}>
              <a onClick={onClose}>
                <MobileIconLinkContainer isActive={router.asPath === `/learn`}>
                  <MobileIconWrapper>
                    <TrainingSvg />
                  </MobileIconWrapper>{' '}
                  <p style={{ marginBottom: 0 }}>
                    <T keyName="Learn More" defaultValue="Learn More" />
                  </p>
                </MobileIconLinkContainer>
              </a>
            </Link>
            <div onClick={handleHelpClicked}>
              <MobileIconLinkContainer isActive={false}>
                <MobileIconWrapper>
                  <QuestionMarkSvg />
                </MobileIconWrapper>{' '}
                <p style={{ marginBottom: 0 }}>{t('Help')}</p>
              </MobileIconLinkContainer>
            </div>
            {!workspace &&
              userAccessBadge?.role === 'Admin' &&
              userAccessBadge?.license.coarseLevel === LicenseCoarseLevels.enterprise &&
              userAccessBadge?.license.fineLevel !== LicenseFineLevels.trial &&
              !isFetching && (
                <MobileIconLinkContainer isActive={false} onClick={handleCreateTeam}>
                  <MobileIconWrapper>
                    <GroupSvg />
                  </MobileIconWrapper>{' '}
                  <p style={{ marginBottom: 0 }}>
                    <T
                      keyName="+ Create School Library"
                      defaultValue="+ Create School Library"
                    />
                  </p>
                </MobileIconLinkContainer>
              )}
            {workspace && (
              <>
                <Divider />
                <p
                  style={{
                    marginBottom: 0,
                    fontSize: 14,
                    color: theme.colors.greyDark,
                  }}
                >
                  <T keyName="Shared Library" defaultValue="Shared Library" />
                </p>
                <Link href={`/${tenantId}/workspace/${workspace.id}`}>
                  <a onClick={onClose}>
                    <MobileIconLinkContainer
                      isActive={
                        router.asPath === `/${tenantId}/workspace/${workspace.id}`
                      }
                    >
                      <MobileIconWrapper>
                        <GroupSvg />
                      </MobileIconWrapper>{' '}
                      <p style={{ marginBottom: 0 }}>{workspace.title}</p>
                    </MobileIconLinkContainer>
                  </a>
                </Link>
              </>
            )}
          </div>
          <div>
            <div onClick={handleSignOut}>
              <MobileIconLinkContainer isActive={false}>
                <MobileIconWrapper>
                  <SignOutSvg />
                </MobileIconWrapper>{' '}
                <p style={{ marginBottom: 0 }}>
                  <T keyName="Logout" defaultValue="Logout" />
                </p>
              </MobileIconLinkContainer>
            </div>
          </div>
        </MobileMenuItemsContainer>
      </MobileCloseContext.Provider>
    </Drawer>
  );
};
const ShortLogo = () => {
  return (
    <ShortLogoContainer>
      {!isProduction() ? <CuripodDevSvg /> : <LogoSvg />}
    </ShortLogoContainer>
  );
};
const MainLogo = () => (
  <Link href="/">
    <a
      style={{
        margin: 0,
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        marginRight: 30,
      }}
    >
      <ShortLogo />
    </a>
  </Link>
);

const MobileIconWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  margin-right: 20px;
  svg {
    height: 20px;
    width: 20px;
  }
`;

const MobileIconLinkContainer = styled.div<{ isActive: boolean }>`
  width: 100%;
  color: ${theme.colors.black};
  display: flex;
  cursor: pointer;
  border-radius: 20px;
  background-color: ${({ isActive }) => (isActive ? '#F3E8D8' : 'transparent')};
  align-items: center;
  padding: 5px 15px;
  margin-bottom: 10px;
  transition: all 0.2s ease-in;

  & p {
    font-weight: bold;
    font-size: 16px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const MenuContainer = styled.div<{ $isLandingPage?: boolean }>`
  display: flex;
  gap: 20px;
  padding: 20px;

  h3 {
    padding-left: 10px;
    font-size: 20px;
  }

  li {
    background-color: ${theme.colors.paleBeige} !important;
    transition: background-color 0.2s ease-in;
    border-radius: 20px;
    a {
      color: ${theme.colors.black};
    }

    &:hover {
      background-color: #f3e8d8 !important;
    }
  }
  .ant-divider-vertical {
    height: 150px;
    align-self: center;
  }

  ${({ $isLandingPage }) =>
    $isLandingPage &&
    css`
      li {
        background-color: ${theme.colors.background} !important;
        a {
          color: ${theme.colors.black};
        }
        span {
          color: ${theme.colors.black};
        }

        &:hover {
          background-color: #00000022 !important;
        }
      }

      h3 {
        color: ${theme.colors.black};
      }
    `}
`;

const ShortLogoContainer = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  svg {
    height: 60px;
    width: 80px;
    color: ${theme.colors.orange};
  }
`;

const RootGroupContainer = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const Container = styled.div`
  z-index: ${zIndexes('TOPBAR')};
  position: fixed;
  padding: 6px 0px;
  top: 0;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${theme.colors.topbar};
  box-shadow: 0px 4px 4px rgb(0, 0, 0, 0.1);
  height: 60px;
  min-height: 60px;
`;

const GlobalStyles = createGlobalStyle<{
  $isLandingPage?: boolean;
  $isRightAligned?: boolean;
}>`
  .ant-dropdown.color-dropdown {
    background-color: transparent !important; 
    position: fixed !important;
    top: 60px !important;
    left: ${({ $isRightAligned, $isLandingPage }) =>
      $isRightAligned && !$isLandingPage ? 'unset' : '130px'} !important;
    right: ${({ $isRightAligned, $isLandingPage }) =>
      $isRightAligned && !$isLandingPage ? '130px' : 'unset'} !important;
    min-height: 200px;
  }
  .color-dropdown .ant-dropdown-menu {
    background-color: ${theme.colors.paleBeige};
  }
  .color-dropdown .ant-dropdown-menu-item:hover {
    background-color: #00000022;
  }

  ${({ $isLandingPage }) =>
    $isLandingPage &&
    css`
      .ant-dropdown.color-dropdown {
        background-color: transparent !important;
        position: fixed !important;
        top: 60px !important;
        min-height: 200px;
      }
      .color-dropdown .ant-dropdown-menu {
        background-color: ${theme.colors.background};
      }
      .color-dropdown .ant-dropdown-menu-item:hover {
        background-color: #00000022;
      }
    `}
    
    ${({ $isRightAligned }) =>
      $isRightAligned
        ? css`
            .ant-dropdown.color-dropdown {
              right: 260px !important;
              left: unset !important;
            }
          `
        : css`
            .ant-dropdown.color-dropdown {
              left: 260px !important;
            }
          `}
    
    }


`;

const DropdownMenuColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  p,
  a,
  span {
    font-family: Nunito;
    font-weight: bold;
  }

  p {
    font-weight: bold;
    line-height: 22px;
    font-size: 14px;
    margin-bottom: 10px;
  }
  a {
    text-decoration: unset;
    line-height: 20px;
  }
  h3 {
    font-size: 1.17em;
    line-height: 1.5715;
    margin-bottom: 0.5em;
    margin-top: 0;
  }
  button {
    background-color: ${theme.colors.paleBeige};
    border: none;
    color: ${theme.colors.black};
    box-shadow: none;
    font-size: 14px;
    transition: all 0.2s ease-in;

    &:hover,
    &:focus {
      background-color: ${theme.colors.blue};
      color: ${theme.colors.white};
    }
  }
`;

const ViewAllLink = styled.span`
  display: block;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  font-size: 14px;
  font-weight: 700;
  color: ${theme.colors.orange};
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: ${theme.colors.orangeDark};
  }
`;

const MobileContainer = styled(Container)<{ $isTransparent?: boolean }>`
  position: fixed;
  height: 60px;
  padding: 14px 20px 14px 20px;
  justify-content: space-between !important;
  align-items: center;
  color: ${theme.colors.black};
  h1 {
    color: ${theme.colors.black};
  }

  ${({ $isTransparent }) =>
    $isTransparent &&
    css`
      background-color: transparent;
      box-shadow: none;
      padding: 14px 20px 14px 20px;
    `}
`;

const MobileMenuItemsContainer = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  @media (max-width: 360px) {
    width: 80%;
    margin-left: 60px !important;
  }
`;
const HeaderMenuContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0px 0px 20px 5px;
`;

const WidthContainer = styled.div`
  color: white;
  display: flex;
  height: 60px;
  width: 100%;
  padding: 0px 20px;
  justify-content: space-between;
  align-items: center;
`;

const NewProfileMenuItem = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { accessBadge: userAccessBadge } = useAccessBadge();
  const router = useRouter();
  const { filteredTenants: tenants, hasAccessToTenantByUrl } = useTenants(true);
  const { tenantId } = useRouterMatch();

  const { signOutAuto } = useSignOut();
  const metrics = useMetrics();

  const ContainerRef = useRef<HTMLDivElement>(null);

  const handleOutSideClick = useCallback(() => {
    setIsOpen(false);
  }, []);

  const [isInviting, setIsInviting] = useState<{
    visible: boolean;
    role: 'student' | 'mentor';
    isSignup?: boolean;
  }>();

  const handleInvite = (
    invite: { visible: boolean; role: 'student' | 'mentor'; isSignup?: boolean },
    from: string,
  ) => {
    metrics.logEvent('Invite.Init', { from });
    handleToggleProfileMenu();
    setIsInviting(invite);
  };

  useOnOutsideClick(ContainerRef, handleOutSideClick);

  const handleLogout = () => {
    metrics.logEvent('TopBar.Profile.Signout');
    signOutAuto();
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleOpen = () => {
    metrics.logEvent('TopBar.Profile.Open');
    setIsOpen(true);
  };

  const handleOpenProfile = () => {
    handleClose();
  };

  const handleToggleProfileMenu = () => {
    if (isOpen) {
      handleClose();
    } else {
      handleOpen();
    }
  };

  const animationProps = {
    initial: { opacity: 0 },
    exit: { opacity: 0 },
    animate: {
      opacity: 1,
    },
    transition: {
      duration: 0.15,
    },
  };

  const activeTenantId = tenants?.user.activeTenantId;

  if (!activeTenantId) {
    return (
      <ProfileDefault
        radius={40}
        color={theme.colors.white}
        icon={<CuripodNormal />}
        backgroundColor={theme.colors.white}
      />
    );
  }

  const currentTenantId = getDestinationTenantFromUrl();

  const tenantName = tenants?.user?.availableTenants
    .find(t => t.tenantId === currentTenantId)
    ?.tenantName.substring(0, 30);

  return (
    <NotificationsWrapper style={{ marginRight: 8 }}>
      {isInviting && isInviting.role && (
        <InvitationModal
          open={!!(isInviting && isInviting.visible)}
          onDone={() => setIsInviting(undefined)}
          role={isInviting?.role}
          isSignup={isInviting?.isSignup}
        />
      )}
      <SmartProfile
        onClick={() => {
          if (hasAccessToTenantByUrl) {
            handleToggleProfileMenu();
          } else {
            router.push(`/`);
          }
        }}
      />
      <AnimatePresence>
        {isOpen && hasAccessToTenantByUrl && (
          <ProfileDropdownContainer ref={ContainerRef}>
            <motion.div {...animationProps}>
              <ProfileMenu>
                <ProfileMenuItem onClick={handleOpenProfile}>
                  <Link href={`/${tenantId}/profile`}>
                    <a>
                      <ProfileInfoItem>
                        <SmartProfile />
                        <h3>{userAccessBadge?.user.username}</h3>
                        <span>{userAccessBadge?.user.email}</span>
                        <span style={{ color: '#a5a5a5' }}>{tenantName}</span>
                      </ProfileInfoItem>
                    </a>
                  </Link>
                </ProfileMenuItem>
                <MenuDivider>
                  <Line />
                </MenuDivider>

                <Link href={`/${tenantId}/profile`}>
                  <a>
                    <ProfileMenuItem onClick={handleOpenProfile}>
                      <span>
                        <T keyName="Settings" defaultValue="Settings" />
                      </span>
                    </ProfileMenuItem>
                  </a>
                </Link>
                {userAccessBadge?.role === 'Admin' && (
                  <ProfileMenuItem
                    onClick={() =>
                      handleInvite({ visible: true, role: 'mentor' }, 'Topbar')
                    }
                  >
                    <span>
                      <T
                        keyName="Invite teachers to tenantName"
                        defaultValue="Invite teachers to {tenantName}"
                        params={{ tenantName }}
                      />
                    </span>
                  </ProfileMenuItem>
                )}

                <MenuDivider>
                  <Line />
                </MenuDivider>
                <ProfileMenuItem $disableHover style={{ padding: 20 }}>
                  <Button
                    onClick={handleLogout}
                    height={'32px'}
                    width="100%"
                    type="ghost"
                  >
                    <T keyName="Logout" defaultValue="Logout" />
                  </Button>
                </ProfileMenuItem>
              </ProfileMenu>
            </motion.div>
          </ProfileDropdownContainer>
        )}
      </AnimatePresence>
    </NotificationsWrapper>
  );
};

const BottomBar = () => {
  const { router, tenantId, parentFolderId, workspaceId } = useRouterMatch();
  const metrics = useMetrics();
  const isNewCreatePageEnabled = useFlag('enableNewCreatePage');

  const { trackEvent } = useIntercom();
  // Make sure the intercom launcher is hidden
  useHideIntercom();
  const createLessonLink = useMemo(() => {
    if (isNewCreatePageEnabled) return `/${tenantId}/home`;
    if (parentFolderId && workspaceId)
      return `/${tenantId}/lessons/new/edit?workspaceId=${workspaceId}&parentFolderId=${parentFolderId}`;
    if (workspaceId) return `/${tenantId}/lessons/new/edit?workspaceId=${workspaceId}`;
    if (parentFolderId)
      return `/${tenantId}/lessons/new/edit?parentFolderId=${parentFolderId}`;
    return `/${tenantId}/lessons/new/edit`;
  }, [parentFolderId, tenantId, workspaceId, isNewCreatePageEnabled]);

  const handleCreateTemplateMobile = () => {
    metrics.logEvent('Lessons.NewTemplate.Start', {
      path: router.pathname,
      placement: 'mobileSidebar',
      workspaceId: workspaceId,
    });
    router.replace(createLessonLink);
    trackEvent('PresentationCreated');
  };

  return (
    <>
      <BottomBarContainer>
        <WidthContainer>
          <div style={{ width: '100%', display: 'flex', justifyContent: 'space-around' }}>
            <BottomBarButton $isActive={router.asPath === `/${tenantId}/lessons`}>
              <Link href={`/${tenantId}/lessons`}>
                <a>
                  <IconWrapper width={'18px'} height={'18px'}>
                    <FolderSvg />
                  </IconWrapper>
                  <span>
                    <T keyName="Lessons" defaultValue="Lessons" />
                  </span>
                </a>
              </Link>
            </BottomBarButton>
            <BottomBarButton
              $isActive={false}
              onClick={() => {
                handleCreateTemplateMobile();
              }}
            >
              <Link href={`/${tenantId}/home`}>
                <a>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '5px',
                    }}
                  >
                    <IconWrapper width={'18px'} height={'18px'}>
                      <PlusSvg />
                    </IconWrapper>
                    <span>
                      <T keyName="New" defaultValue="New" />
                    </span>
                  </div>
                </a>
              </Link>
            </BottomBarButton>

            <BottomBarButton $isActive={router.asPath === `/${tenantId}/reports`}>
              <Link href={`/${tenantId}/reports`}>
                <a>
                  <IconWrapper width={'18px'} height={'18px'}>
                    <ChartSvg />
                  </IconWrapper>
                  <span>
                    <T keyName="Reports" defaultValue="Reports" />
                  </span>
                </a>
              </Link>
            </BottomBarButton>
            <BottomBarButton
              styles={{ background: 'none' }}
              $isActive={router.asPath === `/${tenantId}/reports`}
            >
              <Link href={`/${tenantId}/profile`}>
                <a>
                  <NewProfileMenuItem key="profile" />
                </a>
              </Link>
            </BottomBarButton>
          </div>
        </WidthContainer>
      </BottomBarContainer>
    </>
  );
};

const BottomBarButton = styled(Button)<{ $isActive: boolean }>`
  background: ${({ $isActive }) => (!$isActive ? theme.colors.paleBeige : '#f1eae0')};
  color: ${theme.colors.black};
  box-shadow: none;
  padding: 5px;
  width: 50px;
  font-size: 10px;
  transition: 0.2s;

  a {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 5px;
  }

  span {
    height: 12px;
    line-height: 12px;
  }

  svg {
    margin-right: 0;
  }

  &:hover,
  &:focus {
    transform: scale(1.1);
    background-color: #f1eae0;
    color: ${theme.colors.black};
  }
`;

const BottomBarContainer = styled.div`
  z-index: ${zIndexes('TOPBAR')};
  position: fixed;
  padding: 6px 0px;
  bottom: 0;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${theme.colors.paleBeige};
  box-shadow: 0px 4px 4px rgb(0, 0, 0, 0.1);
  height: 60px;
  min-height: 60px;
`;

const IconClickWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px;
  border-radius: 42px;
  height: 42px;
  cursor: pointer;
  width: 42px;
  ${BaseTransition}
  svg {
    height: 23px;
    width: 23px;
  }
  &:hover {
    background-color: rgba(192, 192, 192, 0.3);
  }
`;

const NotificationsWrapper = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ClickableIconWrapper = styled.button<{ $hasBackground?: boolean }>`
  height: 100%;
  margin-top: 0px;
  border: none;
  background-color: transparent;
  &:focus,
  &:active {
    outline: none;
  }
  cursor: pointer;
  transition: 0.2s;
  &:hover {
    transform: scale(1.1);
  }
  display: flex;
  align-items: center;
  justify-content: center;
  svg {
    height: 28px;
    width: 28px;
  }

  ${({ $hasBackground }) =>
    $hasBackground &&
    css`
      height: unset;
      margin-top: 10px;
      padding: 10px;
      border-radius: 5px;
      background-color: ${theme.colors.paleBeige};
    `}
`;
const ProfileInfoItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 10px 30px;

  h3,
  span {
    text-align: center;
    font-weight: bold;
  }
  h3 {
    color: ${theme.colors.black};
  }
  &&span {
    color: #5f6368;
  }
`;

const MenuDivider = styled.div`
  width: 100%;
  padding: 0px 5px;
`;

const Line = styled.div`
  height: 1px;
  background-color: #a5a5a5;
  width: 100%;
`;

const ProfileMenuItem = styled.li<{ $disableHover?: boolean; $active?: boolean }>`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);

  padding: 5px 20px;

  ${({ $disableHover }) =>
    !$disableHover &&
    css`
      &:hover {
        background-color: ${theme.colors.greyLight};
      }
    `}
  span {
    color: ${theme.colors.black};
    white-space: nowrap;
    font-weight: bold;
  }
  &:first-child {
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;
  }
  ${({ $disableHover }) =>
    $disableHover &&
    css`
      cursor: default;
    `}

  ${({ $active }) =>
    $active &&
    css`
      background-color: ${theme.colors.orange};
      span {
        color: ${theme.colors.white} !important;
      }
    `};
`;

const ProfileMenu = styled.ul`
  display: flex;
  flex-direction: column;
  border-radius: 10px;
  box-shadow: 0px 2px 20px rgba(0, 0, 0, 0.2);
  background-color: ${theme.colors.white};
  padding-inline-start: 0px;
  transform-origin: right;
`;

const ProfileDropdownContainer = styled.div`
  position: absolute;
  top: 65px;
  right: 0px;
  transform-origin: right;
  z-index: ${zIndexes('TOPBAR_DROPDOWN')};
`;

export const IconWrapperDown = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  svg {
    transform-origin: center;
    transform: rotate(90deg);
    height: 16px;
    width: 16px;
  }
`;

const GroupContainer = styled(RootGroupContainer)<{
  $includeMargin?: boolean;
}>`
  ${({ $includeMargin }) =>
    $includeMargin &&
    css`
      & > * {
        margin: 0 10px;
      }
    `}

  & > *:last-child {
    margin: 0px !important;
  }
`;
